import { Tooltip2 } from '@blueprintjs/popover2';
import { Icon } from 'antd';
import { getBookingTheme } from '../components/roster-utilities';
import { IRosterBooking } from '../types';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { FaUserTimes } from 'react-icons/fa';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { Avatar, ThemeIcon, Group, Text, Center } from '@good/ui/core';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import { ROSTER_TAB } from '../components/roster-common-enums';
import { RosterCard } from './roster-card';
import { CardTime } from './card-time';

const WifiIcon = () => {
  return (
    <ThemeIcon color='violet' variant='transparent' size={18} fz={13}>
      <Icon type='wifi' />
    </ThemeIcon>
  );
};

const UserCrossIcon = () => {
  return (
    <ThemeIcon variant='transparent' radius='50%' size={18} color='red'>
      <Center w='70%' h='70%'>
        <FaUserTimes />
      </Center>
    </ThemeIcon>
  );
};

const ClockCheckIcon = ({
  shiftSlotStatus,
}: {
  shiftSlotStatus: ShiftSlotStatus | undefined;
}) => {
  const pending = shiftSlotStatus === ShiftSlotStatus.PENDING;

  return (
    <ThemeIcon
      variant='light'
      radius='50%'
      size={18}
      color={pending ? 'var(--mantine-color-orange-4)' : 'var(--mantine-color-ocean-3)'}
    >
      <Center fz={13}>
        <Icon type={pending ? 'clock-circle' : 'check-circle'} />
      </Center>
    </ThemeIcon>
  );
};

interface BookingCardProps {
  booking: IRosterBooking;
  tab: ROSTER_TAB;
  rowIndex?: number;
}

export const BookingCard = ({ booking, tab, rowIndex }: BookingCardProps) => {
  const { startDateTime, endDateTime, status: bookingStatus, serviceName, scheduleName } = booking;
  const mStartDate = moment(startDateTime);
  const mEndDate = moment(endDateTime);
  const startTimeDisplay = mStartDate.format('h:mm a');
  const endTimeDisplay = mEndDate.format('h:mm a');
  const { bgColor, borderColor } = getBookingTheme(bookingStatus);
  const onSelectRosterItem = useBookings((selector) => selector.onSelectRosterItem);
  const selectedRosterItem = useBookings((selector) => selector.selectedRosterItem);
  const isSelected = selectedRosterItem?.bookingId === booking.bookingId;
  const isCancelled = !!_.find(
    [
      BookingStatus.BUSINESS_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED_WITH_FEE,
      BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
    ],
    (status) => status === booking.status,
  );
  const name = `${booking.workerFirstName ?? ''} ${booking.workerLastName ?? ''}`;

  return (
    <RosterCard
      bg={bgColor}
      borderColor={borderColor}
      endDateTime={booking.endDateTime}
      startDateTime={booking.startDateTime}
      onSelect={() => onSelectRosterItem({ item: booking, type: 'customerBooking', tab })}
      rowIndex={rowIndex}
      selected={isSelected}
      tab={tab}
      topSection={
        <Group wrap='nowrap' gap={8} align='center'>
          <StatusTag status={bookingStatus} size='sm' />
          {booking.serviceType === ServiceType.INDIVIDUAL && (
            <Tooltip2
              content={
                <Text c='white'>
                  {booking.workerId
                    ? name
                    : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING
                      ? 'Published'
                      : 'No team member assigned'}
                </Text>
              }
              position='bottom-right'
            >
              <Group gap={2} align='center'>
                {booking.workerId ? (
                  <>
                    <Avatar size={18} name={name} color='initials' />
                    <ClockCheckIcon shiftSlotStatus={booking.shiftSlotStatus} />
                  </>
                ) : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ? (
                  <WifiIcon />
                ) : !isCancelled ? (
                  <UserCrossIcon />
                ) : (
                  <></>
                )}
              </Group>
            </Tooltip2>
          )}
        </Group>
      }
    >
      <div className='whitespace-nowrap text-overflow-ellipsis'>
        <CardTime startTime={startTimeDisplay} endTime={endTimeDisplay} />
        <Group wrap='nowrap' gap={4} align='center'>
          <BookingTypeIcon
            size='xs'
            type={
              booking.bookingType === BookingType.ACTIVITY_RECORD ? BookingType.ACTIVITY_RECORD : booking.serviceType
            }
          />
          <Text size='sm'>
            {booking.serviceType === ServiceType.GROUP && scheduleName ? scheduleName : serviceName}
          </Text>
        </Group>
      </div>
    </RosterCard>
  );
};
